import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Box, Calendar, IconButton, Stack, Icons } from '@curves/ui-base';

import { parseDate } from '@Util/moment';

const WorkoutCalendarBox = forwardRef(({
                                currentDate,
                                sourceData = { workoutData: [], measureRangeDate: [] },
                                calendarToShow = 1,
                                handleNext,
                                handlePrev,
                            }, ref) => {
    const today = parseDate();
    const yearMonthText = parseDate(currentDate, 'YYYY/MM');
    const [controller, setController] = useState({
        isPrev: true,
        isNext: true,
    });

    const cacheKeyFormat = 'YYYY/MM';
    let cache = useRef({});
    const { measureRangeDate = [] } = sourceData;

    const onClickNext = () => {
        if (typeof handleNext === 'function') handleNext(calendarToShow);
    };

    const onClickPrev = () => {
        if (typeof handlePrev === 'function') handlePrev(calendarToShow);
    };

    /**
     * 判斷指定日期是否為運動日
     * @param {dayjs.Dayjs} mDate - 要判斷的日期
     * @returns {boolean} 是否為運動日
     */
    const isWorkoutDay = mDate => {
        const date = mDate.date();
        const monthKey = mDate.format("YYYY/MM");
        const monthData = cache.current[monthKey];

        if (!monthData || !monthData.dateMap) return false;
        return monthData.dateMap.hasOwnProperty(date);
    };

    /**
     * 判斷指定日期是否為跨店運動日
     * @param {dayjs.Dayjs} mDate - 要判斷的日期
     * @returns {boolean} 是否為跨店運動日
     */
    const isCrossClubWorkout = mDate => {
        if (!isWorkoutDay(mDate)) return false;

        const date = mDate.date();
        const monthKey = mDate.format("YYYY/MM");
        const monthData = cache.current[monthKey];

        if (!monthData || !monthData.dateMap) return false;

        const recordIndex = monthData.dateMap[date];
        const checkInRecord = monthData.items[recordIndex];

        return checkInRecord?.isCross;
    };

    /**
     * 判斷指定日期是否為量測日
     * @param {dayjs.Dayjs} mDate - 要判斷的日期
     * @returns {boolean} 是否為量測日
     */
    const isMeasureDay = mDate => {
        if (measureRangeDate.length === 0) return false;

        const dateString = mDate.format("YYYY-MM-DD");
        return measureRangeDate.includes(dateString);
    };

    /**
     * 初始化指定月份的運動資料快取
     * 將 workoutData 轉換為以日期為 key 的映射表，方便快速查詢
     */
    const initializeCacheForMonth = monthKey => {
        if (cache.current.hasOwnProperty(monthKey)) return;

        const { workoutData = [] } = sourceData;
        if (!Array.isArray(workoutData)) return;

        workoutData.forEach(monthData => {
            const { year, month, days = [] } = monthData;
            const formattedMonth = String(month).padStart(2, "0");
            const cacheKey = parseDate(`${year}-${formattedMonth}`, "YYYY/MM");

            // 建立日期到索引的映射表：{ 1: 0, 5: 1, 10: 2, ... }
            const dateToIndexMap = {};
            days.forEach((day, index) => {
                dateToIndexMap[day] = index;
            });

            // 儲存到快取：包含原始資料和日期映射表
            cache.current[cacheKey] = {
                ...monthData,
                dateMap: dateToIndexMap,
            };
        });
    };

    /**
     * 判斷日期應該套用的 CSS className
     * @param {dayjs.Dayjs} mDate - 要判斷的日期
     * @returns {Object} 包含 workout, crossClub, measure 的布林值物件
     */
    const handleRoleDayClassName = mDate => {
        const monthKey = parseDate(mDate, "YYYY/MM");

        // 確保該月份的資料已初始化到快取中
        initializeCacheForMonth(monthKey);

        // 判斷各種狀態
        const isWorkout = isWorkoutDay(mDate);
        const isCrossClub = isCrossClubWorkout(mDate);
        const isMeasure = isMeasureDay(mDate);

        return {
            workout: isWorkout && !isCrossClub, // 同店運動日
            'cross-club': isCrossClub, // 跨店運動日
            measure: isMeasure, // 量測日
        };
    };
    
    useImperativeHandle(ref, () => ({
        clearCache: (assignDate) => {
            const key = parseDate(assignDate, cacheKeyFormat);
            if (key) {
                // 清除指定 key 的暫存
                delete cache.current[key];
            } else {
                // 清除所有暫存
                cache.current = {};
            }
        },
        hasCache: (assignDate) => {
            const key = parseDate(assignDate, cacheKeyFormat);
            // 檢查指定 key 是否已有資料
            return cache.current.hasOwnProperty(key);
        },
        getCacheKeys: () => {
            // 取得所有已暫存的 keys
            return Object.keys(cache.current);
        }
    }));

    useEffect(() => {
        if (!currentDate) return;
        if (parseDate(currentDate, 'YYYY-MM') >= parseDate(today, 'YYYY-MM')) {
            setController((prevState) => ({
                ...prevState,
                isNext: false,
            }));
        } else {
            setController((prevState) => ({
                ...prevState,
                isNext: true,
            }));
        }
        // eslint-disable-next-line
    }, [yearMonthText, currentDate]);

    if (!sourceData) return null;

    return (
        <React.Fragment>
            <Box className={clsx('workout-calendar-box')}>
                <IconButton className="workout-calendar-box-controller prev" onClick={onClickPrev} disabled={!controller.isPrev}>
                    <Icons.NavigateBefore />
                </IconButton>
                <IconButton className="workout-calendar-box-controller next" onClick={onClickNext} disabled={!controller.isNext}>
                    <Icons.NavigateNext />
                </IconButton>
                <Stack direction="column" alignItems="center" spacing={3}>
                    {Array.from({ length: calendarToShow }).map((_, i) => {
                        let currentMonth = dayjs(currentDate).subtract(calendarToShow - i - 1, 'month');
                        return (
                            <Calendar
                                key={currentMonth.format('YYYY/MM')}
                                className="workout-calendar-box-calendar"
                                year={currentMonth.year()}
                                month={currentMonth.month()}
                                handleRoleDayClassName={handleRoleDayClassName}
                            />
                        );
                    })}
                </Stack>
            </Box>
        </React.Fragment>
    );
});

export default WorkoutCalendarBox;
